import Service, { service } from '@ember/service';
import { EnvoyDeviceSDK } from '@envoy/device-sdk';
import type { ConnectionState, DeviceMessage, DeviceSDKConfig, IEnvoyDeviceSDK } from '@envoy/device-sdk';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import type MetricsService from 'garaje/services/metrics';
import type SplitioService from 'garaje/services/splitio';
import type { MqttMode } from 'garaje/utils/mqtt-types';
import { v4 as uuid } from 'uuid';

const CLIENT_BINDING_TOKEN_KEY = 'envoy:mqtt:clientBindingToken';

interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function getOrCreateSessionBindingToken(storage: SessionStorageLike = sessionStorage): string {
  let token = storage.getItem(CLIENT_BINDING_TOKEN_KEY);
  if (!token) {
    token = uuid();
    storage.setItem(CLIENT_BINDING_TOKEN_KEY, token);
  }
  return token;
}

export type MqttMessageHandler = (msg: DeviceMessage) => void;

export default class MqttConnectionService extends Service {
  @service declare splitio: SplitioService;
  @service declare metrics: MetricsService;

  @tracked connectionState: ConnectionState = { type: 'disconnected' };

  #sdk: IEnvoyDeviceSDK | null = null;
  #generation = 0;
  #messageHandler: MqttMessageHandler | null = null;

  get mqttMode(): MqttMode {
    const treatment = this.splitio.getTreatment('company', 'dashboard-mqtt-mode');
    if (treatment === 'shadow' || treatment === 'primary') return treatment;
    return 'off';
  }

  get isEnabled(): boolean {
    return this.mqttMode !== 'off';
  }

  async start(context: { locationId?: string; zoneId?: string }, onMessage: MqttMessageHandler): Promise<void> {
    if (!this.isEnabled) return;

    // Tear down any existing connection first (location→zone or zone→location switch)
    await this.stop();

    const generation = ++this.#generation;
    const sdk = this.#createSdk(context);
    this.#sdk = sdk;
    this.#messageHandler = onMessage;

    sdk.on('message', onMessage);
    sdk.on('stateChange', (state: ConnectionState) => {
      if (this.#generation === generation) {
        this.connectionState = state;
      }
    });
    sdk.on('log', (entry) => {
      if (entry.level === 'error') {
        console.error('[MQTT]', entry.message, entry.attributes); // eslint-disable-line no-console
      }
    });

    try {
      await sdk.start();
    } catch (e) {
      console.error('[MQTT] Failed to start:', e); // eslint-disable-line no-console
      this.metrics.logMonitorEvent('MQTT_START_ERROR', { error: (e as Error).message });
    }
  }

  async stop(): Promise<void> {
    const sdk = this.#sdk;
    if (!sdk) return;

    // Bump generation so any in-flight stateChange callbacks are ignored
    this.#generation++;

    if (this.#messageHandler) {
      sdk.off('message', this.#messageHandler);
      this.#messageHandler = null;
    }

    this.#sdk = null;

    try {
      await sdk.stop();
    } catch (e) {
      console.warn('[MQTT] Error during stop:', e); // eslint-disable-line no-console
    }
  }

  get isConnected(): boolean {
    return this.#sdk?.currentState.type === 'connected';
  }

  willDestroy(): void {
    super.willDestroy();
    void this.stop();
  }

  #createSdk(context: { locationId?: string; zoneId?: string }): IEnvoyDeviceSDK {
    const sdkConfig: DeviceSDKConfig = {
      baseURL: config.devicesHost,
      // 'garaje' is supported by the devices service but not yet in the SDK's Product type
      product: 'garaje' as DeviceSDKConfig['product'],
      telemetryProvider: () => ({
        location_id: context.locationId,
        zone_id: context.zoneId,
        client_binding_token: this.#getOrCreateBindingToken(),
      }),
      consoleLogging: { prefix: '[EnvoyDeviceSDK:garaje]' },
    };

    return new EnvoyDeviceSDK(sdkConfig) as IEnvoyDeviceSDK;
  }

  #getOrCreateBindingToken(): string {
    return getOrCreateSessionBindingToken();
  }
}

declare module '@ember/service' {
  interface Registry {
    'mqtt-connection': MqttConnectionService;
  }
}
